import React, { useState, useEffect } from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { useSelector } from "react-redux";
import { Typography } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import PersonIcon from "@material-ui/icons/Person";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import WarningIcon from "@material-ui/icons/Warning";
import CompanionSummaryCard from "./CompanionSummaryCard";
import { useTranslation } from "../../../../../../../hooks/useTranslation";
import Button from "../../../../../../../components/common/PL/Button/Button";
import FormTextInput from "../../../../../../../components/common/PL/TextInput/FormTextInput";
import FormSelect from "../../../../../../../components/common/PL/Select/FormSelect";
import FormDatePicker from "../../../../../../../components/common/PL/DatePicker/FormDatePicker";
import FormPicturePreview from "../../../../../../../components/common/PL/PicturePreview/FormPicturePreview";
import FormDocumentPicturePreview from "../../../../../../../components/common/PL/PicturePreview/FormDocumentPicturePreview";
import FormDocumentPreviewForm from "../../../../../../../components/common/PL/PicturePreview/FormDocumentPreviewForm";
import { genderOptions } from "../../../../utils/genderOptions";
import {
  isUnderAge,
  normalizeSexValue,
  parseBirthDateValue,
} from "../../../../utils/visitorPersonalData";
import {
  validateDocumentNumber,
  validateEmail,
  validateNotExpired,
} from "../../../../utils/registroIngresoSchema";
import { parseApiDate } from "../../../../utils/apiDates";
import useArtCompanies from "../../../../../../../hooks/useArtCompanies";
import DialogConfirmation from "../../../../../../../components/common/PL/Dialog/DialogConfirmation";
import CompanionScanModal from "./CompanionScanModal";
import "./VehicleContentCompanionDataForm.css";
import { RegistrationMode } from "../../../../registroIngresoNavigation";
import { countryListData } from "../../../../../../../utils/countries";
import { RootState } from "../../../../../../../redux/reducers/rootReducer";
import { useStore } from "../../../../../../../hooks/useStore";
import UserSDK from "../../../../../../../passapp-sdk/user";
import { useRegistroIngresoSession } from "../../../../session/RegistroIngresoSessionContext";
import AccessTypeMethodCard from "../../../../../../../components/common/PL/Cards/AccessTypeMethodCard/AccessTypeMethodCard";
import AccessTypeResultCard from "../../../../../../../components/common/PL/Cards/AccessTypeResultCard/AccessTypeResultCard";
import { ACCESS_METHOD_OPTIONS } from "../../../../utils/accessMethodItems";
import AuthorizationsSection from "../../../../../../../components/common/PL/Forms/AccessTypeFormContent/AuthorizationsSection";
import "../../../../../../../components/common/PL/Forms/AccessTypeFormContent/AccessTypeFormContent.css";
import {
  mapPassTypeToAccessMethod,
  mapPassTypeToSource,
} from "../../../../utils/guestTypeUtils";
import TransportCompanionAuthorizationCard from "../../../AccessTypeForm/TransportContentForm/TransportCompanionAuthorizationCard";
import AutorizacionEgresoCard from "../../../AccessTypeForm/TransportContentForm/AutorizacionEgresoCard";
import { EntryRegistrationFormValues } from "../../../types";


/** Documentación de ART del acompañante. Sólo transportistas: el acompañante del camión también
 * trabaja, así que se le pide la misma cobertura que al chofer. Vive en su propio componente para
 * que el catálogo de ART recién se pida cuando el bloque se muestra. */
const CompanionArtFields: React.FC<{ index: number; disabled?: boolean }> = ({
  index,
  disabled = false,
}) => {
  const { companies: artCompanyOptions } = useArtCompanies();
  const { watch, setValue } = useFormContext();
  const artCompany = watch(`companions.${index}.companionArtCompany`);

  // Igual que en el chofer: los usuarios viejos traen la ART como texto libre (`artCompanyName`)
  // en vez del id del catálogo, así que se resuelve por nombre para que el desplegable la muestre.
  useEffect(() => {
    if (!artCompany || !artCompanyOptions.length) return;
    if (artCompanyOptions.some((option) => option.value === artCompany)) return;

    const byName = artCompanyOptions.find(
      (option) => option.label.trim().toLowerCase() === String(artCompany).trim().toLowerCase()
    );
    setValue(`companions.${index}.companionArtCompany`, byName ? String(byName.value) : "");
  }, [artCompany, artCompanyOptions, index, setValue]);

  return (
    <>
      <FormSelect
        name={`companions.${index}.companionArtCompany`}
        options={artCompanyOptions}
        label="Compañía de ART *"
        placeholder="-completar-"
        rules={{ required: "La compañía de ART es obligatoria" }}
        disabled={disabled}
        isSearchable
        focusFirstOptionOnOpen
      />
      <FormTextInput
        name={`companions.${index}.companionArtPolicyNumber`}
        label="Número de Póliza ART *"
        placeholder="-completar-"
        rules={{ required: "El número de póliza es obligatorio" }}
        disabled={disabled}
      />
      <FormDatePicker
        name={`companions.${index}.companionArtExpirationDate`}
        label="Fecha de Vencimiento ART *"
        placeholder="-completar-"
        variant="pl"
        rules={{
          required: "La fecha de vencimiento es obligatoria",
          validate: validateNotExpired,
        }}
        disabled={disabled}
      />
      <FormDocumentPreviewForm
        name={`companions.${index}.companionArtPhoto`}
        label="Foto de póliza ART"
        compact
        displayVariant="file"
        readOnly={disabled}
      />
    </>
  );
};

const CompanionAuthorizationsForm: React.FC<{
  index: number;
  disabled?: boolean;
}> = ({ index, disabled = false }) => {
  const { watch, setValue, register, formState: { errors } } = useFormContext<EntryRegistrationFormValues>();
  const { authStore } = useStore();
  const { tabs, activeTabId } = useRegistroIngresoSession();
  const allCompanies = useSelector((state: RootState) => state.auth.companies);
  const allowTransport = useSelector((state: RootState) => state.auth.allowTransport);

  // "Transporte" sólo se ofrece como tipo de ingreso si el edificio lo tiene habilitado.
  const accessMethodOptions = allowTransport
    ? ACCESS_METHOD_OPTIONS
    : ACCESS_METHOD_OPTIONS.filter((option) => option.type !== "transport");
  const userSdk = React.useMemo(() => new UserSDK(), []);
  const { t, BundleKey } = useTranslation();

  const [companionAuthorizations, setCompanionAuthorizations] = useState<any[]>([]);
  const [isLoadingAuths, setIsLoadingAuths] = useState(false);

  const documentNumber = watch(`companions.${index}.companionDocumentNumber`);
  const documentCountry = watch(`companions.${index}.companionDocumentCountry`);
  const selectedAccessMethod = watch(`companions.${index}.selectedAccessMethod`);
  const isEmployee = watch(`companions.${index}.isEmployee`);
  const employeeWorkplaces = watch(`companions.${index}.employeeWorkplaces`) || [];

  const passId = watch(`companions.${index}.passId`);
  const passType = watch(`companions.${index}.passType`);
  const passGuestsType = watch(`companions.${index}.passGuestsType`);
  const isBuildingMismatch = watch(`companions.${index}.isBuildingMismatch`);

  const driverAccessMethod = watch("selectedAccessMethod");
  const isDriverTransport = driverAccessMethod === "transport";
  const transportFlujo = watch("transportFlujo");
  const transportAutorizacionFull = watch("transportAutorizacionFull");
  const transportAutorizacionEgreso = watch("transportAutorizacionEgreso");

  // Default companion access method reactively based on driver access method
  useEffect(() => {
    if (!selectedAccessMethod && !isEmployee && driverAccessMethod) {
      setValue(`companions.${index}.selectedAccessMethod`, driverAccessMethod);
    }
  }, [selectedAccessMethod, isEmployee, driverAccessMethod, index, setValue]);

  // 1. Employee auto-detection hook when DNI/Country changes
  useEffect(() => {
    if (!documentNumber || !documentCountry) {
      return;
    }

    const checkEmployeeStatus = async () => {
      try {
        const response = await userSdk.findUserByDNI(
          authStore.token,
          documentNumber,
          documentCountry
        );
        const userData = response?.data as any;
        if (userData) {
          const isEmp = userData.employee === true;
          setValue(`companions.${index}.isEmployee`, isEmp);
          setValue(`companions.${index}.employeeWorkplaces`, userData.workplaces || []);
          if (isEmp) {
            setValue(`companions.${index}.selectedAccessMethod`, "employee");
          }
        } else {
          setValue(`companions.${index}.isEmployee`, false);
          setValue(`companions.${index}.employeeWorkplaces`, []);
        }
      } catch (err) {
        console.error("Error looking up companion employee status:", err);
      }
    };

    checkEmployeeStatus();
  }, [documentNumber, documentCountry, authStore.token, index, setValue, userSdk]);

  // 2. Fetch active authorizations when access method or details change
  useEffect(() => {
    if (!documentNumber || !documentCountry || !selectedAccessMethod || isEmployee) {
      setCompanionAuthorizations([]);
      return;
    }

    const fetchAuths = async () => {
      setIsLoadingAuths(true);
      try {
        let results: any[] = [];
        if (selectedAccessMethod === "visitor") {
          const authRes = await userSdk.getAuthorizations(
            authStore.token,
            documentNumber,
            documentCountry
          );
          const rawAuths = (authRes?.data?.authorizations || []).map((a: any) => ({
            ...a,
            _source: "authorization",
          }));
          const rawInvites = (authRes?.data?.invites || []).map((i: any) => ({
            ...i,
            _source: "invite",
          }));
          results = [...rawAuths, ...rawInvites];
        } else {
          const authRes = await userSdk.getPrevalidations(
            authStore.token,
            selectedAccessMethod,
            documentNumber,
            documentCountry
          );
          results = authRes?.data || [];
        }
        setCompanionAuthorizations(results);

        if (passId && !isBuildingMismatch) {
          const matchingAuth = results.find(
            (a: any) => String(a.id) === String(passId)
          );
          if (matchingAuth) {
            const passSource =
              matchingAuth._source ||
              mapPassTypeToSource(passType, passGuestsType);
            if (passSource === "invite") {
              setValue(`companions.${index}.invites`, [matchingAuth.id]);
            } else if (passSource === "authorization") {
              setValue(`companions.${index}.authorizations`, [matchingAuth.id]);
            } else {
              setValue(`companions.${index}.prevalidation`, [matchingAuth.id]);
            }
            if (matchingAuth.companyId || matchingAuth.company) {
              setValue(
                `companions.${index}.authorizationCompany`,
                matchingAuth.companyId || matchingAuth.company
              );
            }
          }
        }
      } catch (err) {
        console.error("Error fetching companion authorizations:", err);
        setCompanionAuthorizations([]);
      } finally {
        setIsLoadingAuths(false);
      }
    };

    fetchAuths();
  }, [
    documentNumber,
    documentCountry,
    selectedAccessMethod,
    isEmployee,
    authStore.token,
    userSdk,
    passId,
    passType,
    passGuestsType,
    isBuildingMismatch,
    index,
    setValue,
  ]);

  // 3. Driver selected authorizations (Destinos heredados del conductor)
  const driverInvites = watch("invites") || [];
  const driverAuths = watch("authorizations") || [];
  const driverPrevals = watch("prevalidation") || [];
  const driverConfirmations = watch("employeeConfirmationsData") || [];
  const driverIsEmployee = watch("isEmployee");
  const driverWorkplaces = watch("employeeWorkplaces") || [];

  const selectedDriverAuths = React.useMemo(() => {
    // When driver is an employee, inherit from their workplaces
    if (driverIsEmployee && driverWorkplaces.length > 0) {
      return driverWorkplaces.map((wp: any, idx: number) => {
        const companyObj = allCompanies?.find((c: any) => c.id === wp.company);
        const companyName = companyObj?.name || companyObj?.description || wp.company || "N/A";
        let managerName = "N/A";
        let managerId = "";
        let managerPhone = "N/A";
        if (wp.manager) {
          if (typeof wp.manager === "object") {
            managerId = wp.manager.id || wp.manager;
            managerName =
              `${wp.manager.firstname || ""} ${wp.manager.lastname || ""}`.trim() || "N/A";
            managerPhone = wp.manager.phone || "N/A";
          } else {
            managerId = wp.manager;
            managerName = wp.managerName || wp.manager;
          }
        }
        return {
          id: `employee-wp-${wp.company}-${idx}`,
          realId: managerId,
          companyId: wp.company || "",
          company: companyName,
          managerName,
          managerPhone,
          status: "Empleado",
          type: "employee",
        };
      });
    }

    // When driver is not an employee, inherit from selected authorizations
    const activeTab = tabs.find((tab) => tab.id === activeTabId) || tabs[0];
    const allTabAuths = activeTab?.authorizations || [];
    const selectedAuths = allTabAuths.filter((auth: any) => {
      if (auth._source === "invite") return driverInvites.includes(auth.id);
      if (auth._source === "authorization") return driverAuths.includes(auth.id);
      return driverPrevals.includes(auth.id);
    });

    const formattedAuths = selectedAuths.map((auth: any) => {
      const companyObj = allCompanies?.find((c: any) => c.id === auth.company);
      const company = companyObj?.name || companyObj?.description || auth.guestCompany || "N/A";
      const host = auth.host || auth.manager;
      const managerName = host ? `${host.firstname} ${host.lastname}`.trim() : "N/A";
      const managerPhone = host?.phone || "N/A";
      return {
        id: auth.id,
        companyId: auth.company || "",
        company,
        managerName,
        managerPhone,
        status: auth._source === "invite" ? "Invitación" : "Autorización",
        type: "authorization",
        _source: auth._source,
      };
    });

    const formattedConfirmations = driverConfirmations.map((c: any) => {
      if (c.type === "guard") {
        const companyObj = allCompanies?.find((co: any) => co.id === c.company);
        const companyName = companyObj?.name || companyObj?.description || "N/A";
        const host = c.host;
        const managerName = host ? `${host.firstname} ${host.lastname}`.trim() : "N/A";
        const managerPhone = host?.phone || "N/A";
        return {
          id: c.id,
          realId: host?.id || "",
          companyId: c.company || "",
          company: companyName,
          managerName,
          managerPhone,
          status: "Empleado",
          type: "employee",
        };
      }

      return {
        id: c.id,
        realId: c.realId,
        companyId: c.companyId || c.company,
        company: c.company,
        managerName: c.employee,
        managerPhone: c.phone || "N/A",
        status: "Empleado",
        type: "employee",
      };
    });

    return [...formattedAuths, ...formattedConfirmations];
  }, [
    tabs,
    activeTabId,
    driverInvites,
    driverAuths,
    driverPrevals,
    driverConfirmations,
    driverIsEmployee,
    driverWorkplaces,
    allCompanies,
  ]);

  // Register the virtual authorization field & calculate if an authorization has been selected
  const companionConfirmations = watch(`companions.${index}.employeeConfirmations`) || [];
  const companionPrevals = watch(`companions.${index}.prevalidation`) || [];
  const companionInvites = watch(`companions.${index}.invites`) || [];
  const companionAuths = watch(`companions.${index}.authorizations`) || [];

  const isValid =
    isEmployee ||
    isDriverTransport ||
    companionConfirmations.length > 0 ||
    companionPrevals.length > 0 ||
    companionInvites.length > 0 ||
    companionAuths.length > 0;

  const companionErrors = (errors?.companions as any)?.[index];
  const hasError = !!companionErrors?.authorizationSelected;

  useEffect(() => {
    register(`companions.${index}.authorizationSelected`, { required: true });
  }, [register, index]);

  useEffect(() => {
    setValue(`companions.${index}.authorizationSelected`, isValid ? "true" : "", {
      shouldValidate: !!companionErrors,
    });
  }, [isValid, setValue, index, companionErrors]);

  if (isEmployee) {
    return (
      <div className="companionAuthorizationsSection" style={{ marginTop: 24 }}>
        <Typography style={{ fontWeight: 700, marginBottom: 16 }} variant="h5">
          Autorizaciones
        </Typography>
        <div className="employeeCardsContainer">
          {employeeWorkplaces.map((wp: any, idx: number) => {
            const companyObj = allCompanies?.find((c: any) => c.id === wp.company);
            const companyName = companyObj?.name || companyObj?.description || wp.company || "N/A";
            let managerName = "N/A";
            if (wp.manager) {
              if (typeof wp.manager === "object") {
                managerName =
                  `${wp.manager.firstname || ""} ${wp.manager.lastname || ""}`.trim() || "N/A";
              } else {
                managerName = wp.managerName || wp.manager;
              }
            }
            return (
              <AccessTypeResultCard
                key={idx}
                label="Empleado"
                variant="employee"
                showCheckbox={false}
                leftIcon={<AssignmentIndIcon />}
                managerLabel="Manager"
                managerName={managerName}
                phoneLabel="Empresa"
                managerPhone={companyName}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (isDriverTransport) {
    const entranceReady = transportFlujo === "entrada" && !!transportAutorizacionFull;
    const exitReady = transportFlujo === "salida" && !!transportAutorizacionEgreso;

    return (
      <div className="companionAuthorizationsSection" style={{ marginTop: 24 }}>
        <Typography style={{ fontWeight: 700, marginBottom: 16 }} variant="h5">
          Autorización
        </Typography>
        {entranceReady && (
          <TransportCompanionAuthorizationCard autorizacion={transportAutorizacionFull} />
        )}
        {exitReady && (
          <AutorizacionEgresoCard
            autorizacion={transportAutorizacionEgreso}
            isSelected
            isExpanded
            onSelect={() => {}}
            onToggleExpand={() => {}}
            onReenviar={() => {}}
            onVerificarControl={() => {}}
            observations={{}}
            onObservacionTextChange={() => {}}
            onObservacionPhotoChange={() => {}}
            disabled
          />
        )}
        {!entranceReady && !exitReady && (
          <div className="companionAuthorizationsEmptyState">
            El acompañante compartirá la autorización del transportista una vez que este la
            seleccione.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="companionAuthorizationsSection" style={{ marginTop: 24 }} data-field-name={`companions.${index}.authorizationSelected`}>
      {/* Tipo de ingreso */}
      <div style={{ marginBottom: 24 }}>
        <div className="accessTypeMethodGrid">
          {accessMethodOptions.map((option) => (
            <AccessTypeMethodCard
              key={option.type}
              labelKey={option.labelKey}
              icon={React.createElement(option.icon, { fontSize: "inherit" })}
              selected={selectedAccessMethod === option.type}
              onClick={() => {
                setValue(`companions.${index}.selectedAccessMethod`, option.type);
              }}
            />
          ))}
        </div>
      </div>

      <AuthorizationsSection
        id={`companion-${index}-access-type-authorizations`}
        className="transparentAccordionCard"
        disabled={disabled}
        hasError={hasError}
        isCompanion={true}
        searchFieldName={`companions.${index}.search`}
        confirmationsFieldName={`companions.${index}.employeeConfirmations`}
        confirmationsDataFieldName={`companions.${index}.employeeConfirmationsData`}
        prevalidationsFieldName={`companions.${index}.prevalidation`}
        invitesFieldName={`companions.${index}.invites`}
        authorizationsFieldName={`companions.${index}.authorizations`}
        companyFieldName={`companions.${index}.authorizationCompany`}
        authorizationsList={companionAuthorizations}
        isLoadingAuths={isLoadingAuths}
        inheritedAuthorizations={selectedDriverAuths}
      />
    </div>
  );
}

export default function VehicleContentCompanionDataForm({
  disabled = false,
  mode = "entry",
}: {
  disabled?: boolean;
  mode?: RegistrationMode;
}) {
  const { t, BundleKey } = useTranslation();
  const {
    control,
    watch,
    trigger,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "companions",
  });

  // "Registrar salida sin ingreso previo" también usa mode="exit", pero no hay evento de entrada
  // del que hereden los acompañantes: sin esta distinción la sección se ocultaba igual que en un
  // egreso real, y no había forma de cargar a nadie que haya salido acompañado.
  const entranceEvent = watch("entranceEvent");
  const isDeterministicExit = mode === "exit" && !!entranceEvent;

  const driverAccessMethod = watch("selectedAccessMethod");
  const isDriverTransport = driverAccessMethod === "transport";

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isNewCompanion, setIsNewCompanion] = useState(false);
  const [initialCompanionData, setInitialCompanionData] = useState<any>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const selfieRef = React.useRef<any>(null);

  // Al guardar, el formulario abierto —alto— se reemplaza por la tarjeta resumen —baja—: la página
  // se acorta de golpe y el scroll queda donde estaba, que después del cambio es cualquier lado.
  // Se guarda el índice recién cerrado para llevar la vista hasta su tarjeta.
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const [savedIndex, setSavedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (savedIndex === null) return;
    // Cada acompañante es un hijo directo de la lista —tarjeta o formulario—, así que la posición
    // en `children` es el índice del acompañante.
    const card = listRef.current?.children[savedIndex];
    card?.scrollIntoView({ behavior: "smooth", block: "center" });
    setSavedIndex(null);
  }, [savedIndex]);

  // Egreso con ingreso previo: los acompañantes ya se resolvieron con el evento de entrada, así que
  // esta sección no vuelve a pedirlos.
  useEffect(() => {
    if (isDeterministicExit) {
      setValue("companions", []);
    }
  }, [isDeterministicExit, setValue]);

  // Índice del acompañante que se está agregando y todavía no se guardó, para poder descartarlo si
  // la sección se desmonta. En un ref porque lo lee la limpieza del efecto de abajo, que se arma
  // una sola vez y si no vería los valores del primer render.
  const pendingNewCompanionIndexRef = React.useRef<number | null>(null);
  useEffect(() => {
    pendingNewCompanionIndexRef.current = isNewCompanion ? editingIndex : null;
  }, [isNewCompanion, editingIndex]);

  // Irse de la sección sin guardar equivale a cancelar (ver handleCancel). Sin esto el acompañante
  // quedaba en `companions` —RHF conserva los valores aunque el campo se desmonte— y al volver se
  // lo veía como una tarjeta ya cargada, porque el estado local de edición sí se pierde: pasa al
  // mover el medio de acceso a "A pie" y volver a "Vehículo", que desmonta todo este bloque.
  useEffect(() => {
    return () => {
      const index = pendingNewCompanionIndexRef.current;
      if (index === null) return;
      const current = getValues("companions") || [];
      setValue(
        "companions",
        current.filter((_: any, i: number) => i !== index)
      );
    };
  }, [getValues, setValue]);

  if (isDeterministicExit) {
    return null;
  }

  const handleAddCompanion = () => {
    if (disabled) return;
    setIsScanModalOpen(true);
  };

  const handleScanFound = (userData: any, dni: string, country: string) => {
    if (disabled) return;
    const user = userData as any;
    const isEmp = user.employee === true;

    const passAccessMethod = user.passType
      ? mapPassTypeToAccessMethod(user.passType, user.passGuestsType)
      : undefined;

    const resolvedAccessMethod = user.isBuildingMismatch
      ? ""
      : (isEmp
          ? "employee"
          : (passAccessMethod || driverAccessMethod || "visitor"));

    append({
      companionName: user.firstname || "",
      companionLastName: user.lastname || "",
      companionDocumentNumber: dni,
      companionDocumentCountry: country,
      companionEmail: user.email || "",
      companionCellphone: user.phone || "",
      companionSex: normalizeSexValue(user.sex),
      companionBirthdate: parseBirthDateValue(user.birthdate),
      companionSelfie: user.facePhoto || "",
      companionDniFront: user.frontDNI || "",
      companionDniBack: user.backDNI || "",
      // Documentación de ART ya cargada en el usuario (sólo se muestra en transportistas).
      companionArtCompany: user.artCompany || user.artCompanyName || "",
      companionArtPolicyNumber: user.artPolicyNumber || "",
      companionArtExpirationDate: parseApiDate(user.artExpirationDate),
      companionArtPhoto: user.artPhoto1 || null,
      isEmployee: isEmp,
      employeeWorkplaces: user.workplaces || [],
      selectedAccessMethod: resolvedAccessMethod,
      passId: user.passId || "",
      passType: user.passType || "",
      passGuestsType: user.passGuestsType || "",
      isBuildingMismatch: user.isBuildingMismatch || false,
      // Sólo "Guardar" lo pone en true (ver handleSave): hasta entonces vive en el form para poder
      // bindear sus campos, pero no cuenta como cargado y no se manda al backend.
      companionConfirmed: false,
    });
    setIsScanModalOpen(false);
    setIsNewCompanion(true);
    setInitialCompanionData(null);
    setEditingIndex(fields.length);
  };

  const handleScanNotFound = (dni: string, country: string) => {
    if (disabled) return;
    append({
      companionDocumentNumber: dni,
      companionDocumentCountry: country,
      selectedAccessMethod: driverAccessMethod || "visitor",
      companionConfirmed: false,
    });
    setIsScanModalOpen(false);
    setIsNewCompanion(true);
    setInitialCompanionData(null);
    setEditingIndex(fields.length);
  };

  const handleScanSkip = () => {
    if (disabled) return;
    append({
      selectedAccessMethod: driverAccessMethod || "visitor",
      companionConfirmed: false,
    });
    setIsScanModalOpen(false);
    setIsNewCompanion(true);
    setInitialCompanionData(null);
    setEditingIndex(fields.length);
  };

  const handleStartEdit = (index: number) => {
    if (disabled) return;
    setInitialCompanionData(getValues(`companions.${index}`));
    setIsNewCompanion(false);
    setEditingIndex(index);
  };

  const handleSave = async (index: number) => {
    if (disabled) return;
    const isValid = await trigger(`companions.${index}`, { shouldFocus: true });
    if (isValid) {
      // Recién acá el acompañante cuenta como cargado: `processCompanions` (useEvent.ts) descarta
      // todo lo que no tenga este flag, así que un acompañante a medio completar que el guardia
      // abandona sin guardar nunca llega al backend.
      setValue(`companions.${index}.companionConfirmed`, true);
      setEditingIndex(null);
      setIsNewCompanion(false);
      setInitialCompanionData(null);
      setSavedIndex(index);
    }
  };

  const handleCancel = (index: number) => {
    if (isNewCompanion) {
      remove(index);
    } else if (initialCompanionData) {
      setValue(`companions.${index}`, initialCompanionData);
      // Cancelar una edición cierra el formulario igual que guardar: la vista tiene que seguir a la
      // tarjeta. Al cancelar un acompañante nuevo no hay adónde ir, la fila se va con él.
      setSavedIndex(index);
    }
    clearErrors(`companions.${index}.authorizationSelected`);
    setEditingIndex(null);
    setIsNewCompanion(false);
    setInitialCompanionData(null);
  };

  const handleDeleteClick = (index: number) => {
    if (disabled) return;
    setDeleteIndex(index);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteIndex !== null) {
      remove(deleteIndex);
    }
    setIsDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  const companionsValues = watch("companions") || [];

  const getCompanionRequired = (message: string) => ({
    required: message,
  });

  return (
    <div className="vehicleCompanionSection">
      <div className="vehicleCompanionSectionHeader">
        <Typography className="vehicleCompanionTitle">
          {t(BundleKey.DETAIL_COMPATION_TITLE)}
        </Typography>
        {editingIndex === null && !disabled && (
          <Button
            className="vehicleCompanionAddButton"
            onClick={handleAddCompanion}
            variant="gradient"
          >
            <PersonIcon className="vehicleCompanionAddButtonIcon" />
            <span className="vehicleCompanionAddButtonLabel">
              {t(BundleKey.DETAIL_COMPATION_SUBMIT_ADD)}
            </span>
          </Button>
        )}
      </div>

      <div
        className="vehicleCompanionList"
        ref={listRef}
        style={fields.length > 0 ? { marginBottom: "24px" } : {}}
      >
        {fields.map((field, index) => {
          const isEditing = editingIndex === index;
          const companionData = companionsValues[index] || {};
          const fullName =
            `${companionData.companionName || ""} ${
              companionData.companionLastName || ""
            }`.trim() || t(BundleKey.DETAIL_COMPATION_ITEM_TITLE);
          const documentId = companionData.companionDocumentNumber;

          if (!isEditing) {
            return (
              <CompanionSummaryCard
                key={field.id}
                fullName={fullName}
                documentId={documentId}
                isActionsDisabled={disabled || editingIndex !== null}
                onEdit={() => handleStartEdit(index)}
                onDelete={() => handleDeleteClick(index)}
              />
            );
          }

          return (
            <div key={field.id} className="vehicleCompanionSubcard vehicleCompanionItem">
              <div className="vehicleCompanionSubcardHeader">
                <div
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <Typography style={{ fontWeight: 700 }} variant="h5">
                    {`${t(BundleKey.DETAIL_COMPATION_ITEM_TITLE)} ${index + 1}`}
                  </Typography>
                </div>
              </div>

              <div className="vehicleCompanionImagesContainer">
                <div className="vehicleCompanionSelfieWrapper">
                  <FormPicturePreview
                    ref={selfieRef}
                    name={`companions.${index}.companionSelfie`}
                    label={t(BundleKey.DETAIL_COMPATION_SELFIE)}
                    allowImageViewer
                    allowClear
                    previewHeight={150}
                  />
                </div>

                <div className="vehicleCompanionDocumentWrapper">
                  <FormDocumentPicturePreview
                    allowClear
                    allowImageViewer
                    name={`companions.${index}.companionDniFront`}
                    label={t(BundleKey.DETAIL_COMPATION_DNI_FRONT)}
                    previewHeight={150}
                    placeholderHeight={150}
                  />
                </div>

                <div className="vehicleCompanionDocumentWrapper">
                  <FormDocumentPicturePreview
                    allowClear
                    allowImageViewer
                    name={`companions.${index}.companionDniBack`}
                    label={t(BundleKey.DETAIL_COMPATION_DNI_BACK)}
                    previewHeight={150}
                    placeholderHeight={150}
                  />
                </div>
              </div>

              <div className="vehicleCompanionFieldsGrid">
                <FormTextInput
                  name={`companions.${index}.companionName`}
                  label={`${t(BundleKey.DETAIL_COMPATION_FIRSTNAME)} *`}
                  placeholder="-completar-"
                  rules={getCompanionRequired("El nombre del acompañante es obligatorio")}
                />
                <FormTextInput
                  name={`companions.${index}.companionLastName`}
                  label={`${t(BundleKey.DETAIL_COMPATION_LASTNAME)} *`}
                  placeholder="-completar-"
                  rules={getCompanionRequired("El apellido del acompañante es obligatorio")}
                />
                <FormSelect
                  name={`companions.${index}.companionDocumentCountry`}
                  label={`${t(BundleKey.DETAIL_COMPATION_DOCUMENT_COUNTRY)} *`}
                  placeholder="-completar-"
                  options={countryListData.map((c) => ({ value: c.value, label: c.name }))}
                  rules={getCompanionRequired(
                    "El país del documento del acompañante es obligatorio"
                  )}
                />
                <FormTextInput
                  name={`companions.${index}.companionDocumentNumber`}
                  label={`${t(BundleKey.DETAIL_COMPATION_DOCUMENT_NUMBER)} *`}
                  placeholder="-completar-"
                  rules={{
                    validate: (value: unknown) => {
                      return validateDocumentNumber(value as string);
                    },
                  }}
                />
                <FormTextInput
                  name={`companions.${index}.companionEmail`}
                  label={t(BundleKey.DETAIL_COMPATION_EMAIL)}
                  placeholder="-completar-"
                  rules={{ validate: validateEmail }}
                />
                <FormTextInput
                  name={`companions.${index}.companionCellphone`}
                  label={t(BundleKey.DETAIL_COMPATION_CELLPHONE)}
                  placeholder="-completar-"
                />
                <FormSelect
                  name={`companions.${index}.companionSex`}
                  label="Género *"
                  placeholder="-completar-"
                  options={genderOptions}
                  rules={getCompanionRequired("El género del acompañante es obligatorio")}
                />
                <FormDatePicker
                  name={`companions.${index}.companionBirthdate`}
                  label="Fecha de nacimiento"
                  placeholder="-completar-"
                  variant="pl"
                />
                {isUnderAge(parseBirthDateValue(companionData.companionBirthdate)) && (
                  <div className="vehicleCompanionUnderAgeBadge">
                    <WarningIcon className="vehicleCompanionUnderAgeIcon" aria-hidden="true" />
                    MENOR DE EDAD - ACCESO CONDICIONADO
                  </div>
                )}
                {isDriverTransport && <CompanionArtFields index={index} disabled={disabled} />}
              </div>

              <CompanionAuthorizationsForm index={index} disabled={disabled} />

              {((errors?.companions as any)?.[index]?.authorizationSelected) && (
                <div className="vehicleCompanionCallout vehicleCompanionCalloutError" style={{ marginTop: 16 }}>
                  <InfoOutlinedIcon className="vehicleCompanionInfoIcon vehicleCompanionInfoIconError" />
                  <Typography className="vehicleCompanionCalloutText vehicleCompanionCalloutTextError">
                    {t(BundleKey.ERROR_PASS_MUST)}
                  </Typography>
                </div>
              )}

              <div className="vehicleCompanionActions">
                <div className="vehicleCompanionActionsSpacer" />
                <Button
                  className="vehicleCompanionActionButton"
                  variant="outline"
                  onClick={() => handleCancel(index)}
                  type="button"
                >
                  {t(BundleKey.ACTION_CANCEL)}
                </Button>
                <Button
                  className="vehicleCompanionActionButton"
                  onClick={() => handleSave(index)}
                  type="button"
                >
                  {t(BundleKey.ACTION_SAVE)}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <DialogConfirmation
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Eliminar acompañante"
        message={
          deleteIndex !== null ? (
            <>
              Esta seguro que quiere eliminar al acompañante{" "}
              <strong>
                {`${companionsValues[deleteIndex]?.companionName || ""} ${
                  companionsValues[deleteIndex]?.companionLastName || ""
                }`.trim() || t(BundleKey.DETAIL_COMPATION_ITEM_TITLE)}
                {companionsValues[deleteIndex]?.companionDocumentNumber
                  ? `, DNI ${companionsValues[deleteIndex].companionDocumentNumber}`
                  : ""}
              </strong>
              ?
            </>
          ) : (
            ""
          )
        }
        confirmLabel="Eliminar"
        cancelLabel={t(BundleKey.ACTION_CANCEL)}
      />

      <CompanionScanModal
        open={isScanModalOpen}
        onClose={() => setIsScanModalOpen(false)}
        onFound={handleScanFound}
        onNotFound={handleScanNotFound}
        onSkip={handleScanSkip}
      />
    </div>
  );
}
